import { useEffect } from "react";
import { createRootRoute, Outlet } from "@tanstack/react-router";
import { useTranslation } from "@/hooks/useSafeTranslation";
import { useAuth } from "@/contexts/useAuth";
import { isStagingMode } from "@/config";
import { toDisplayString, toSafeReactNode } from "@/lib/utils";
import { apiPost } from "@/api";
import { PostHogPageView } from "@/components/PostHogPageView";
import { ErrorBoundary } from "@/components/ErrorBoundary";

const REFERRAL_STORAGE_KEY = "takelead_referral_code";

function getExtensionAuthRedirect(): string | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const redirect = params.get("redirect");
  if (params.get("extensionAuth") !== "1" || !redirect) return null;
  if (!redirect.startsWith("chrome-extension://")) return null;
  return redirect;
}

function handleReferralLanding(): boolean {
  if (typeof window === "undefined") return false;
  let code: string | null = null;

  const pathname = window.location.pathname;
  const pathMatch = pathname.match(/^\/r\/([^/]+)\/?$/);
  if (pathMatch) code = pathMatch[1].trim();

  if (!code) {
    const hash = window.location.hash.slice(1);
    const hashMatch = hash.match(/^r\/([^/]+)$/);
    if (hashMatch) code = hashMatch[1].trim();
  }

  if (!code) {
    const q = new URLSearchParams(window.location.search);
    code = q.get("referral")?.trim() ?? null;
  }

  if (code) {
    try {
      localStorage.setItem(REFERRAL_STORAGE_KEY, code);
    } catch {
      // ignore
    }
    return true;
  }
  return false;
}

function ExtensionAuthSendToken({
  redirectUrl,
  token,
}: {
  redirectUrl: string;
  token: string;
}) {
  const { t } = useTranslation("common");
  useEffect(() => {
    window.location.href =
      redirectUrl + "#access_token=" + encodeURIComponent(String(token));
  }, [redirectUrl, token]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg p-4">
      <div className="w-full max-w-sm rounded-xl border border-border bg-surface-2 p-8 shadow-md text-center">
        <h1 className="mb-2 text-xl font-semibold text-text">{toSafeReactNode(t("root.title"))}</h1>
        <p className="text-sm text-text-muted">
          {toSafeReactNode(t("root.extensionRedirect"))}
        </p>
      </div>
    </div>
  );
}

function StagingBanner() {
  const { t } = useTranslation("common");
  if (!isStagingMode()) return null;
  return (
    <div
      className="sticky top-0 z-50 flex items-center justify-center gap-2 bg-amber-500/90 px-4 py-1.5 text-center text-sm font-medium text-amber-950"
      role="status"
      aria-label={toDisplayString(t("root.stagingAria"))}
    >
      <span>{toSafeReactNode(t("root.stagingBanner"))}</span>
    </div>
  );
}

function RootComponent() {
  const { state } = useAuth();
  const extensionRedirect = getExtensionAuthRedirect();

  useEffect(() => {
    handleReferralLanding();
  }, []);

  useEffect(() => {
    if (state.status !== "authenticated") return;
    const code = localStorage.getItem(REFERRAL_STORAGE_KEY);
    if (!code) return;
    apiPost("/referrals/attach", state.token, { referralCode: code })
      .then(() => localStorage.removeItem(REFERRAL_STORAGE_KEY))
      .catch(() => {});
  }, [state]);

  if (
    extensionRedirect &&
    state.status === "authenticated"
  ) {
    return (
      <ExtensionAuthSendToken
        redirectUrl={extensionRedirect}
        token={state.token}
      />
    );
  }

  return (
    <>
      {state.status === "authenticated" ? <PostHogPageView /> : null}
      <StagingBanner />
      <ErrorBoundary>
        <Outlet />
      </ErrorBoundary>
    </>
  );
}

export const rootRoute = createRootRoute({
  component: RootComponent,
});
